import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { QRCodeCanvas } from 'qrcode.react';
import { createTranslator, useLanguage } from '../i18n/LanguageContext';
import { tournamentRegistrationTranslations } from '../i18n/translations';
import InvoiceFields, { emptyBilling, billingCopy, billingFieldNames } from './InvoiceFields';

const FUNCTIONS_BASE_URL = 'https://us-central1-mendyx-adafe.cloudfunctions.net';

const functionUrl = (functionName, params = {}) => {
  const url = new URL(`${FUNCTIONS_BASE_URL}/${functionName}`);

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      url.searchParams.set(key, value);
    }
  });

  return url.toString();
};

const postFunctionJson = async (functionName, body) => {
  const response = await fetch(functionUrl(functionName), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    const error = new Error(data.error || `Erreur function ${functionName}: ${response.status}`);
    error.status = response.status;
    error.data = data;
    throw error;
  }

  return data;
};

const duplicateMessage = (reason) => (
  reason === 'email'
    ? "Cette adresse e-mail est deja inscrite pour ce tournoi."
    : "Ce nom d'equipe est deja inscrit pour ce tournoi."
);

const eloCategories = {
  P50: 600,
  P100: 700,
  P150: 800,
  P200: 900,
  P300: 1200,
  P400: 1600,
  P500: 2000,
};

const TournamentRegistration = ({ registrationTournamentId, teamBuilding = false } = {}) => {
  const { language } = useLanguage();
  const t = createTranslator(language, tournamentRegistrationTranslations);
  const tb = createTranslator(language, billingCopy);
  const [billing, setBilling] = useState(emptyBilling);
  const [isTeamBuilding, setIsTeamBuilding] = useState(teamBuilding);
  const [pricePerPerson, setPricePerPerson] = useState(null);
  const { tournamentId: routeTournamentId } = useParams();
  const tournamentId = registrationTournamentId || routeTournamentId;
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    teamName: '',
    email: '',
    newsletter: false,
    consentInfo: false,
    mealOption: false,
    mealQuantity: 0,
    joueur1: {
      nom: '',
      prenom: '',
      phone: '',
      elo: '',
      isLocked: false,
    },
    joueur2: {
      nom: '',
      prenom: '',
      phone: '',
      elo: '',
      isLocked: false,
    },
  });

  const [participantsCount, setParticipantsCount] = useState(0);
  const [limitReached, setLimitReached] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [eloMin, setEloMin] = useState(null);
  const [eloMax, setEloMax] = useState(null);
  const [price, setPrice] = useState(0);
  const [participantsLimit, setParticipantsLimit] = useState(null);
  const [mealOptionEnabled, setMealOptionEnabled] = useState(false);
  const [mealPrice, setMealPrice] = useState(0);
  const [mealDescription, setMealDescription] = useState('');
  const [duplicateStatus, setDuplicateStatus] = useState({
    checking: false,
    exists: false,
    reason: null,
    message: '',
  });

  const participantsLimits = {
    "1": 12,
    "2": 12,
    "3": 12,
    "4": 12,
    "5": 8,
    "6": 8,
    "7": 16,
    "8": 16,
    "9": 8,
    "10": 8,
    "11": 8,
    "12": 16,
    "13": 16,
    "14": 8,
    "15": 8,
    "16": 8,
    "17": 8,
    "18": 16,
    "19": 16,
    "20": 8,
    "21": 8,
    "22": 8,
    "23": 8,
    "24": 8,
    "25": 12,
    "26": 8,
    "27": 8,
    "29": 16,
    "30": 8,
    "31": 8,
    "32": 8,
    "33": 12,
    "34": 16,
    "35": 8,
    "36": 8,
    "37": 8,
    "38": 16,
    "39": 16,
    "40": 16,
    "41": 16,
    "42": 16,
    "43": 16,
    "44": 8,
    "45": 8,
    "46": 8,
    "47": 8,
    "48": 8,
    "49": 8,
    "50": 8,
    "51": 16,
    "52": 12,
    "53": 12,
    "54": 16,
    "55": 16,
    "56": 16,
    "57": 16,
  };

  const imageSrc = `/padel${tournamentId}.jpg`;

  const mealQuantity = mealOptionEnabled && formData.mealOption
    ? Number(formData.mealQuantity || 0)
    : 0;
  const wantsMeal = mealQuantity > 0;
  const basePrice = Number(price || 0);
  const selectedMealPrice = wantsMeal ? mealQuantity * Number(mealPrice || 0) : 0;
  const totalPrice = basePrice + selectedMealPrice;

  const isValidPhone = (phone) => {
    const regex = /^04\d{8}$/;
    return regex.test(phone.trim());
  };

  useEffect(() => {
    const checkPlayer = async () => {
      const phone = formData.joueur1.phone.trim();
      if (!isValidPhone(phone)) return;

      try {
        const response = await fetch(functionUrl('getJoueurByPhone', { phone }));
        const data = await response.json();

        if (data) {
          setFormData((prev) => ({
            ...prev,
            joueur1: {
              ...prev.joueur1,
              nom: data.nom,
              prenom: data.prenom,
              elo: data.elo,
              isLocked: true,
            },
          }));
        } else {
          setFormData((prev) => ({
            ...prev,
            joueur1: {
              ...prev.joueur1,
              elo: '700',
              isLocked: false,
            },
          }));
        }
      } catch (err) {
        console.error('Erreur joueur1:', err);
      }
    };

    checkPlayer();
  }, [formData.joueur1.phone]);

  useEffect(() => {
    const checkPlayer = async () => {
      const phone = formData.joueur2.phone.trim();
      if (!isValidPhone(phone)) return;

      try {
        const response = await fetch(functionUrl('getJoueurByPhone', { phone }));
        const data = await response.json();

        if (data) {
          setFormData((prev) => ({
            ...prev,
            joueur2: {
              ...prev.joueur2,
              nom: data.nom,
              prenom: data.prenom,
              elo: data.elo,
              isLocked: true,
            },
          }));
        } else {
          setFormData((prev) => ({
            ...prev,
            joueur2: {
              ...prev.joueur2,
              elo: '700',
              isLocked: false,
            },
          }));
        }
      } catch (err) {
        console.error('Erreur joueur2:', err);
      }
    };

    checkPlayer();
  }, [formData.joueur2.phone]);

  useEffect(() => {
    const fetchTournamentDetails = async () => {
      try {
        const response = await fetch(functionUrl('protectedGetTournamentRegistrationPublicDetails', { tournamentId }));
        if (!response.ok) throw new Error(`Erreur function tournoi: ${response.status}`);
        const tournamentData = await response.json();

        if (!tournamentData) {
          console.error("Aucun tournoi trouve pour cet ID.");
          return;
        }

        setPrice(Number(tournamentData.price || 0));
        setIsTeamBuilding(teamBuilding || Boolean(tournamentData.isTeamBuilding));
        setPricePerPerson(tournamentData.pricePerPerson ?? null);
        setMealOptionEnabled(Boolean(tournamentData.mealOptionEnabled));
        setMealPrice(Number(tournamentData.mealPrice || 0));
        setMealDescription(tournamentData.mealDescription || '');
        setParticipantsCount(Number(tournamentData.participantsCount || 0));
        setParticipantsLimit(
          Number(
            tournamentData.players ||
            tournamentData.participantsLimit ||
            participantsLimits[tournamentId] ||
            16
          )
        );
        setLimitReached(Boolean(tournamentData.limitReached));
        setEloMin(tournamentData.eloMin ?? null);
        setEloMax(tournamentData.eloMax ?? null);
      } catch (error) {
        console.error('Erreur lors de la recuperation des details du tournoi :', error);
      }
    };

    fetchTournamentDetails();
  }, [tournamentId, teamBuilding]);

  useEffect(() => {
    const teamName = formData.teamName.trim();
    const email = formData.email.trim();

    if (!teamName && !email) {
      setDuplicateStatus({ checking: false, exists: false, reason: null, message: '' });
      return;
    }

    const shouldCheckTeamName = teamName.length >= 2;
    const shouldCheckEmail = email.includes('@') && email.includes('.');

    if (!shouldCheckTeamName && !shouldCheckEmail) {
      setDuplicateStatus({ checking: false, exists: false, reason: null, message: '' });
      return;
    }

    let cancelled = false;
    const timeoutId = window.setTimeout(async () => {
      setDuplicateStatus((prev) => ({ ...prev, checking: true }));

      try {
        const duplicateData = await postFunctionJson('protectedCheckTournamentRegistrationDuplicate', {
          tournamentId,
          teamName: shouldCheckTeamName ? teamName : '',
          email: shouldCheckEmail ? email : '',
        });

        if (cancelled) return;
        setDuplicateStatus({
          checking: false,
          exists: Boolean(duplicateData.exists),
          reason: duplicateData.reason || null,
          message: duplicateData.exists ? duplicateMessage(duplicateData.reason) : '',
        });
      } catch (error) {
        if (cancelled) return;
        console.error('Erreur verification doublon tournoi :', error);
        setDuplicateStatus({ checking: false, exists: false, reason: null, message: '' });
      }
    }, 450);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [formData.teamName, formData.email, tournamentId]);

  const handleChange = (e, key, subkey = null) => {
    const { name, value, type, checked } = e.target;
    const val = type === 'checkbox' ? checked : value;

    if (name === 'mealOption') {
      setFormData((prev) => ({
        ...prev,
        mealOption: checked,
        mealQuantity: checked ? Number(prev.mealQuantity || 1) : 0,
      }));
      return;
    }

    if (key && subkey) {
      setFormData((prev) => {
        const updated = {
          ...prev,
          [key]: {
            ...prev[key],
            [subkey]: val,
          },
        };

        if (
          subkey === 'phone' &&
          !/^(?:\+32|0)4\d{8}$/.test(val.trim())
        ) {
          updated[key].isLocked = false;
        }

        return updated;
      });
    } else {
      setFormData((prev) => ({ ...prev, [name]: val }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isTeamBuilding && billing.invoiceRequested && billingFieldNames.some((field) => !billing[field].trim())) {
      alert(tb('invoiceRequired'));
      return;
    }

    if (!formData.consentInfo) {
      alert(t('consentAlert'));
      return;
    }

    if (!isValidPhone(formData.joueur1.phone) || !isValidPhone(formData.joueur2.phone)) {
      alert(t('phoneAlert'));
      return;
    }

    if (formData.joueur1.phone.trim() === formData.joueur2.phone.trim()) {
      alert(t('samePhone'));
      return;
    }

    const nom1 = formData.joueur1.nom.trim().toLowerCase();
    const prenom1 = formData.joueur1.prenom.trim().toLowerCase();
    const nom2 = formData.joueur2.nom.trim().toLowerCase();
    const prenom2 = formData.joueur2.prenom.trim().toLowerCase();

    if (nom1 === nom2 && prenom1 === prenom2) {
      alert(t('samePlayer'));
      return;
    }

    const totalElo = parseInt(formData.joueur1.elo || 0, 10) + parseInt(formData.joueur2.elo || 0, 10);

    if (
      (eloMin && totalElo < eloMin) ||
      (eloMax && totalElo > eloMax)
    ) {
      alert(t('eloAlert', { total: totalElo, min: eloMin, max: eloMax }));
      return;
    }

    try {
      if (duplicateStatus.exists) {
        alert(duplicateStatus.message || t('duplicate'));
        return;
      }

      const duplicateData = await postFunctionJson('protectedCheckTournamentRegistrationDuplicate', {
        tournamentId,
        teamName: formData.teamName.trim(),
        email: formData.email.trim(),
      });

      if (duplicateData.exists) {
        const message = duplicateMessage(duplicateData.reason);
        setDuplicateStatus({
          checking: false,
          exists: true,
          reason: duplicateData.reason || null,
          message,
        });
        alert(message);
        return;
      }

      if (participantsLimit && participantsCount >= participantsLimit) {
        setLimitReached(true);
        alert(t('limitReached'));
        return;
      }

      const data = await postFunctionJson('protectedCreateTournamentCheckoutPublicV2', {
        ...(isTeamBuilding ? billing : {}),
        tournamentId,
        teamName: formData.teamName,
        email: formData.email,
        price: totalPrice,
        basePrice,
        mealOption: wantsMeal,
        mealQuantity,
        mealPrice: selectedMealPrice,
        mealDescription: wantsMeal ? mealDescription : '',
        totalPrice,
        language,
        newsletter: formData.newsletter,
        consentInfo: formData.consentInfo,
        joueur1: formData.joueur1,
        joueur2: formData.joueur2,
      });

      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error("Aucune URL de session trouvee");
      }
    } catch (error) {
      console.error('Erreur lors de la creation de la session Stripe :', error);
      if (error.data?.reason === 'limitReached') {
        setLimitReached(true);
        alert(t('limitReached'));
        return;
      }
      if (error.status === 409 && error.data?.reason) {
        alert(duplicateMessage(error.data.reason));
        return;
      }
      alert(t('genericError'));
    }
  };

  const closeModal = () => {
    setShowModal(false);
    navigate('/');
  };

  return (
    <section className="container mx-auto my-16 px-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-stretch h-full min-h-[700px]">
        <div className="flex justify-center items-center">
          <div className="relative w-full h-full min-h-[700px] overflow-hidden rounded-lg shadow-lg">
            <img
              src={imageSrc}
              alt={`Tournoi de Padel ${tournamentId}`}
              className="object-contain w-full h-full transition-transform duration-300 hover:scale-105"
            />
          </div>
        </div>

        <div className="p-6 bg-white shadow-lg rounded-r-lg transition-transform duration-300 hover:scale-105">
          <h2 className="text-2xl font-semibold text-[#4c2f8e] mb-4">{t('title')}</h2>

          {limitReached ? (
            <p className="text-red-500">{t('limitReached')}</p>
          ) : (
            <form className="space-y-4" onSubmit={handleSubmit}>
              {isTeamBuilding && <InvoiceFields value={billing} onChange={(event) => {
                const { name, type, checked, value } = event.target;
                setBilling((current) => ({ ...current, [name]: type === 'checkbox' ? checked : value }));
              }} />}

              <div className="flex flex-col">
                <label htmlFor="teamName" className="text-lg font-medium text-gray-700">
                  {t('teamName')}
                </label>
                <input
                  id="teamName"
                  name="teamName"
                  type="text"
                  autoComplete="organization"
                  autoCorrect="off"
                  autoCapitalize="none"
                  spellCheck={false}
                  className="p-2 border rounded-md focus:outline-none focus:ring focus:border-[#4c2f8e]"
                  placeholder={t('teamPlaceholder')}
                  value={formData.teamName}
                  onChange={handleChange}
                  required
                />
                {duplicateStatus.reason === 'teamName' && duplicateStatus.message && (
                  <p className="mt-1 text-sm text-red-600">{duplicateStatus.message}</p>
                )}
              </div>

              {["joueur1", "joueur2"].map((key, index) => (
                <div key={key} className="p-4 border border-gray-300 rounded-md bg-gray-50">
                  <h3 className="text-md font-semibold text-[#4c2f8e] mb-2">
                    {t('player', { number: index + 1 })}
                  </h3>

                  <div className="flex flex-col mb-4">
                    <label className="text-sm text-gray-700 flex items-center gap-2 mb-1">
                      {t('phone')}
                      <div className="flex items-center text-blue-600 text-xs bg-blue-50 px-2 py-1 rounded-full">
                        <span>
                          {t('phoneHint')}
                        </span>
                      </div>
                    </label>

                    <input
                      type="tel"
                      className="p-2 border border-blue-400 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-300 focus:border-blue-500"
                      placeholder="0470707070"
                      value={formData[key].phone}
                      onChange={(e) => handleChange(e, key, 'phone')}
                      required
                    />

                    <div className="mt-2 text-sm text-gray-600 bg-gray-50 border-l-4 border-blue-400 p-3 rounded-md">
                      {t('knownPlayer')}
                      <br />
                      {t('newPlayer')}
                    </div>
                  </div>

                  <div className="flex flex-col mb-2">
                    <label className="text-sm text-gray-700">{t('lastName')}</label>
                    <input
                      type="text"
                      className="p-2 border rounded-md focus:outline-none focus:ring focus:border-[#4c2f8e]"
                      placeholder={t('lastName')}
                      value={formData[key].nom}
                      onChange={(e) => handleChange(e, key, 'nom')}
                      required
                    />
                  </div>

                  <div className="flex flex-col mb-2">
                    <label className="text-sm text-gray-700">{t('firstName')}</label>
                    <input
                      type="text"
                      className="p-2 border rounded-md focus:outline-none focus:ring focus:border-[#4c2f8e]"
                      placeholder={t('firstName')}
                      value={formData[key].prenom}
                      onChange={(e) => handleChange(e, key, 'prenom')}
                      required
                    />
                  </div>

                  <div className="flex flex-col mb-2">
                    <label htmlFor="email" className="text-lg font-medium text-gray-700">
                      {t('contactEmail')}
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      className="p-2 border rounded-md focus:outline-none focus:ring focus:border-[#4c2f8e]"
                      placeholder={t('emailPlaceholder')}
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />
                    {duplicateStatus.reason === 'email' && duplicateStatus.message && (
                      <p className="mt-1 text-sm text-red-600">{duplicateStatus.message}</p>
                    )}
                  </div>

                  {formData[key].isLocked ? (
                    <div className="flex flex-col">
                      <label className="text-sm text-gray-700">{t('eloRanking')}</label>
                      <input
                        type="number"
                        className="p-2 border rounded-md focus:outline-none focus:ring focus:border-[#4c2f8e]"
                        value={formData[key].elo}
                        disabled
                      />
                    </div>
                  ) : (
                    <div className="flex flex-col mb-2">
                      <label className="text-sm text-gray-700">{t('estimatedLevel')}</label>
                      <select
                        className="p-2 border rounded-md focus:outline-none focus:ring focus:border-[#4c2f8e]"
                        value={formData[key].elo}
                        onChange={(e) =>
                          handleChange(
                            { target: { value: parseInt(e.target.value, 10), type: 'select-one' } },
                            key,
                            'elo'
                          )
                        }
                        required
                      >
                        <option value="">{t('chooseCategory')}</option>
                        {Object.entries(eloCategories).map(([label, value]) => (
                          <option key={label} value={value}>
                            {label} - {value} {t('points')}
                          </option>
                        ))}
                      </select>
                      <div className="mt-1 text-xs text-gray-500 italic">
                        {t('eloHint')}
                      </div>
                    </div>
                  )}
                </div>
              ))}

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="consentInfo"
                  name="consentInfo"
                  checked={formData.consentInfo}
                  onChange={handleChange}
                  className="h-4 w-4 text-[#4c2f8e] focus:ring-[#4c2f8e] border-gray-300 rounded"
                  required
                />
                <label htmlFor="consentInfo" className="ml-2 text-gray-700">
                  {t('consent')}
                </label>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="newsletter"
                  name="newsletter"
                  checked={formData.newsletter}
                  onChange={handleChange}
                  className="h-4 w-4 text-[#4c2f8e] focus:ring-[#4c2f8e] border-gray-300 rounded"
                />
                <label htmlFor="newsletter" className="ml-2 text-gray-700">
                  {t('newsletter')}
                </label>
              </div>

              {mealOptionEnabled && (
                <div className="rounded-md border border-purple-100 bg-purple-50 p-3">
                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      id="mealOption"
                      name="mealOption"
                      checked={formData.mealOption}
                      onChange={handleChange}
                      className="mt-1 h-4 w-4 text-[#4c2f8e] focus:ring-[#4c2f8e] border-gray-300 rounded"
                    />
                    <label htmlFor="mealOption" className="ml-2 text-gray-700">
                      {t('addMeal')}
                      {mealPrice > 0 ? ` (${t('perMeal', { price: mealPrice })})` : ''}
                    </label>
                  </div>

                  {formData.mealOption && (
                    <div className="mt-3">
                      <label htmlFor="mealQuantity" className="block text-sm font-medium text-gray-700">
                        {t('mealCount')}
                      </label>
                      <select
                        id="mealQuantity"
                        name="mealQuantity"
                        value={formData.mealQuantity || 1}
                        onChange={handleChange}
                        className="mt-1 w-full p-2 border rounded-md focus:outline-none focus:ring focus:border-[#4c2f8e]"
                      >
                        <option value="1">1 repas{mealPrice > 0 ? ` (+${mealPrice} euros)` : ''}</option>
                        <option value="2">2 repas{mealPrice > 0 ? ` (+${mealPrice * 2} euros)` : ''}</option>
                        <option value="3">3 repas{mealPrice > 0 ? ` (+${mealPrice * 3} euros)` : ''}</option>
                        <option value="4">4 repas{mealPrice > 0 ? ` (+${mealPrice * 4} euros)` : ''}</option>
                      </select>
                    </div>
                  )}

                  {formData.mealOption && mealDescription && (
                    <p className="mt-2 text-sm text-gray-600">
                      {mealDescription}
                    </p>
                  )}
                </div>
              )}

              <p aria-live="polite" className="text-lg font-semibold text-[#4c2f8e]">
                {isTeamBuilding && pricePerPerson !== null && <span className="block text-sm font-normal">2 × {pricePerPerson} € = {basePrice} €</span>}
                {t('price')} : {totalPrice} &euro;
                {wantsMeal && (
                  <span className="block text-sm font-normal text-gray-600">
                    {t('meals', { count: mealQuantity })} : {selectedMealPrice} &euro;
                  </span>
                )}
              </p>

              <button
                type="submit"
                disabled={duplicateStatus.checking || duplicateStatus.exists}
                className={`mt-6 w-full px-6 py-3 bg-gradient-to-r from-[#4c2f8e] to-[#ea4c89] text-white rounded-full shadow-lg transform transition-all duration-300 ${duplicateStatus.checking || duplicateStatus.exists ? 'opacity-60 cursor-not-allowed' : 'hover:scale-110 hover:shadow-2xl'}`}
              >
                {duplicateStatus.checking ? t('checking') : t('register')}
              </button>
            </form>
          )}
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white p-8 rounded-lg shadow-lg w-11/12 md:w-1/2 lg:w-1/3">
            <h3 className="text-xl font-bold mb-4 text-center">{t('confirmed')}</h3>
            <div className="flex flex-col md:flex-row items-center">
              <div className="md:w-1/2">
                <p className="mb-4">
                  <br />
                  {t('finalAfterPayment')}
                  <br />
                  <br />
                  <strong>{t('payment')} :</strong> BE65 0019 9907 2996
                  <br />
                  <strong>{t('price')} :</strong> {totalPrice}€
                  <br />
                  <strong>{t('paymentReference')} :</strong> {t('teamName')}
                  <br />
                  <br />
                  {t('viewPools')}
                  <br />
                  <br />
                  <strong className="text-center">{t('ready')}</strong>
                </p>
              </div>

              <div className="md:w-1/2 flex flex-col items-center mt-4 md:mt-0">
                <p className="text-center mb-2 text-gray-700">{t('scan')}</p>
                <QRCodeCanvas
                  value={`BCD
001
1
SCT
BICDEABCXXX
Padel Mendyx SRL
EUR${totalPrice}.00`}
                  size={150}
                  level="M"
                />
              </div>
            </div>
            <button
              onClick={closeModal}
              className="mt-4 w-full px-4 py-2 bg-gradient-to-r from-[#4c2f8e] to-[#ea4c89] text-white rounded-full"
            >
              {t('close')}
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

export default TournamentRegistration;
